import React from 'react';
import { motion } from 'framer-motion';
import { 
  Code, 
  Database, 
  Zap, 
  Shield, 
  Globe, 
  BarChart3, 
  CheckCircle, 
  ArrowRight,
  Cloud
} from 'lucide-react';
import Navigation from './Navigation';
import Footer from './Footer';

const APIPage: React.FC = () => {
  const apiEndpoints = [
    {
      method: 'GET',
      endpoint: '/api/v1/tailors',
      description: 'Retrieve list of verified tailors',
      parameters: ['location', 'specialty', 'rating', 'limit']
    },
    {
      method: 'POST',
      endpoint: '/api/v1/orders',
      description: 'Create new tailoring orders',
      parameters: ['customer_id', 'tailor_id', 'measurements', 'requirements']
    },
    {
      method: 'GET',
      endpoint: '/api/v1/orders/{id}',
      description: 'Get order details and status',
      parameters: ['order_id']
    },
    {
      method: 'PUT',
      endpoint: '/api/v1/orders/{id}/status',
      description: 'Update order status',
      parameters: ['order_id', 'status', 'notes']
    },
    {
      method: 'GET',
      endpoint: '/api/v1/measurements',
      description: 'Retrieve customer measurements',
      parameters: ['customer_id', 'tailor_id']
    },
    {
      method: 'POST',
      endpoint: '/api/v1/measurements',
      description: 'Store new measurements',
      parameters: ['customer_id', 'measurements_data', 'metadata']
    }
  ];

  const features = [
    {
      icon: <Zap className="w-8 h-8 text-brand-blue" />,
      title: "RESTful API",
      description: "Modern REST API with JSON responses and comprehensive error handling"
    },
    {
      icon: <Shield className="w-8 h-8 text-brand-blue" />,
      title: "OAuth 2.0",
      description: "Secure authentication with OAuth 2.0 and JWT tokens"
    },
    {
      icon: <Database className="w-8 h-8 text-brand-blue" />,
      title: "Real-time Updates",
      description: "WebSocket support for real-time order updates and notifications"
    },
    {
      icon: <Globe className="w-8 h-8 text-brand-blue" />,
      title: "Global CDN",
      description: "Fast response times with global content delivery network"
    },
    {
      icon: <BarChart3 className="w-8 h-8 text-brand-blue" />,
      title: "Analytics",
      description: "Built-in analytics and usage monitoring for your integrations"
    },
    {
      icon: <Cloud className="w-8 h-8 text-brand-blue" />,
      title: "Scalable Infrastructure",
      description: "Auto-scaling infrastructure to handle your growing needs"
    }
  ];

  const pricing = [
    {
      plan: "Developer",
      price: "₹0",
      description: "Perfect for testing and development",
      features: ["1,000 API calls/month", "Basic support", "Documentation access", "Community forum"]
    },
    {
      plan: "Starter",
      price: "₹4,999",
      description: "For small businesses and startups",
      features: ["50,000 API calls/month", "Email support", "Priority queue", "Webhook support"]
    },
    {
      plan: "Professional",
      price: "₹19,999",
      description: "For growing businesses",
      features: ["500,000 API calls/month", "Phone support", "Custom rate limits", "Advanced analytics"]
    },
    {
      plan: "Enterprise",
      price: "Custom",
      description: "For large organizations",
      features: ["Unlimited API calls", "Dedicated support", "Custom integrations", "SLA guarantee"]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-brand-white to-brand-blue/10">
      <Navigation />
      
      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-brand-blue rounded-full shadow-lg">
                <Code className="w-12 h-12 text-white" />
              </div>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Master Ji <span className="text-brand-blue">API</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Powerful APIs to integrate custom tailoring into your applications and workflows
            </p>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              API <span className="text-brand-blue">Features</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Everything you need to build powerful tailoring integrations
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="flex items-center justify-center w-16 h-16 bg-brand-blue/10 rounded-xl mb-4 mx-auto">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">
                  {feature.title}
                </h3>
                <p className="text-gray-600 text-center leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* API Endpoints Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              API <span className="text-brand-blue">Endpoints</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Explore our comprehensive API endpoints for seamless integration
            </p>
          </motion.div>

          <div className="space-y-4">
            {apiEndpoints.map((endpoint, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex items-center space-x-4">
                    <div className={`px-3 py-1 rounded-full text-sm font-semibold ${
                      endpoint.method === 'GET' ? 'bg-green-100 text-green-800' :
                      endpoint.method === 'POST' ? 'bg-blue-100 text-blue-800' :
                      endpoint.method === 'PUT' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {endpoint.method}
                    </div>
                    <code className="text-lg font-mono text-gray-800 bg-gray-100 px-3 py-2 rounded">
                      {endpoint.endpoint}
                    </code>
                  </div>
                  <p className="text-gray-600 md:text-right">
                    {endpoint.description}
                  </p>
                </div>
                {endpoint.parameters.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <p className="text-sm text-gray-500 mb-2">Parameters:</p>
                    <div className="flex flex-wrap gap-2">
                      {endpoint.parameters.map((param, paramIndex) => (
                        <span key={paramIndex} className="px-2 py-1 bg-brand-blue/10 text-brand-blue text-sm rounded">
                          {param}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              API <span className="text-brand-blue">Pricing</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Choose the perfect plan for your integration needs
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {pricing.map((plan, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 ${
                  plan.plan === 'Professional' ? 'ring-2 ring-brand-blue ring-offset-4' : ''
                }`}
              >
                {plan.plan === 'Professional' && (
                  <div className="text-center mb-4">
                    <span className="bg-brand-blue text-white px-3 py-1 rounded-full text-sm font-semibold">
                      Most Popular
                    </span>
                  </div>
                )}
                
                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{plan.plan}</h3>
                  <div className="mb-2">
                    <span className="text-3xl font-bold text-brand-blue">{plan.price}</span>
                    {plan.price !== 'Custom' && <span className="text-gray-500">/month</span>}
                  </div>
                  <p className="text-gray-600 text-sm">{plan.description}</p>
                </div>
                
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span className="text-sm text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <button className="w-full bg-brand-blue text-white py-3 px-4 rounded-lg font-semibold hover:bg-brand-blue/90 transition-colors">
                  Get Started
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-brand-blue/5 to-brand-yellow/5">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              Ready to <span className="text-brand-blue">Integrate</span>?
            </h2>
            
            <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
              Start building powerful tailoring integrations with our comprehensive API
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="px-8 py-4 bg-brand-blue text-white rounded-lg hover:bg-brand-blue/90 transition-colors font-medium flex items-center justify-center space-x-2">
                <span>Get API Key</span>
                <ArrowRight className="w-5 h-5" />
              </button>
              <button className="px-8 py-4 border border-brand-blue text-brand-blue rounded-lg hover:bg-brand-blue/10 transition-colors font-medium">
                View Documentation
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default APIPage;

